import { createTheme, outlinedInputClasses } from '@mui/material';
import '@fontsource/open-sans';
export const theme = createTheme({
    custom: {
        errorColor: '#D32F2F'
    },
    palette: {
        primary: {
            main: '#26890D',
            light: '#4fb332',
            dark: '#1d6209',
            contrastText: '#ffffff'
        },
        text: {
            primary: '#212121',
            secondary: '#595959'
        },
        background: {
            paper: '#FFFFFF',
            default: '#F8F8F8'
        },
        divider: '#EBEBEB'
    },
    typography: {
        fontFamily: ['Open Sans', 'sans-serif'].join(','),
        h1: {},
        h2: {},
        h3: {
            fontFamily: 'Open Sans',
            fontWeight: '400',
            fontSize: '48px',
            lineHeight: '145%'
        },
        h4: {
            fontFamily: 'Open Sans',
            fontWeight: '400',
            fontSize: '34px',
            lineHeight: '41.99px',
            letterSpacing: '.25px'
        },
        h5: {
            fontFamily: 'Open Sans',
            fontWeight: '400',
            fontSize: '24px',
            lineHeight: '32.02px'
        },
        h6: {
            fontFamily: 'Open Sans',
            fontWeight: '600',
            fontSize: '20px',
            lineHeight: '32px',
            letterSpacing: '.15px'
        },
        body1: {
            fontFamily: 'Open Sans',
            fontWeight: '400',
            fontSize: '16px',
            lineHeight: '24px',
            letterSpacing: '.15px'
        },
        subtitle1: {
            fontFamily: 'Open Sans',
            fontWeight: '400',
            fontSize: '16px',
            lineHeight: '28px',
            letterSpacing: '.15px'
        },
        subtitle2: {
            fontFamily: 'Open Sans',
            fontWeight: '600',
            fontSize: '14px',
            lineHeight: '24px',
            letterSpacing: '.17px'
        },
        button: {
            fontFamily: 'Open Sans',
            fontWeight: '600',
            fontSize: '13px',
            lineHeight: '22px',
            letterSpacing: '.46px'
        },
        body2: {
            fontFamily: 'Open Sans',
            fontSize: '14px',
            fontWeight: '400',
            lineHeight: '20px',
            letterSpacing: '0.17000000178813934px'
        },
        caption: {
            fontSize: '12px',
            fontFamily: 'Open Sans',
            fontWeight: '400',
            lineHeight: '19.92px'
        }
    },
    components: {
        // Name of the component
        MuiTab: {
            styleOverrides: {
            // Name of the slot
            }
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    '&.Mui-focused': {
                        color: '#26890D'
                    }
                }
            }
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    height: '40px'
                }
            }
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    [`&.Mui-focused .${outlinedInputClasses.notchedOutline}`]: {
                        borderColor: '#26890D'
                    }
                }
            }
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    backgroundColor: '#0E336A',
                    color: '#fff', // set the font color of the tooltip
                    fontFamily: 'Open Sans', // set the font family of the tooltip
                    fontSize: '10px', // set the font size of the tooltip
                    fontWeight: '600',
                    lineHeight: '14px'
                }
            }
        },
        MuiTypography: {
            defaultProps: {
                variantMapping: {
                // Map the new variant to render a <h1> by default
                }
            }
        },
        MuiButton: {
            styleOverrides: {
                sizeLarge: {
                    height: '42px'
                },
                sizeMedium: {
                    height: '36px'
                },
                sizeSmall: {
                    height: '30px'
                },
                contained: {
                    backgroundColor: '#26890D',
                    color: '#FFFFFF'
                },
                outlinedPrimary: {
                    borderColor: '#26890D',
                    color: '#26890D'
                },
                textPrimary: {
                    color: '#26890D'
                }
            }
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    '&:hover': {
                        backgroundColor: 'rgba(38, 137, 13, 0.04) !important'
                    },
                    '&.Mui-selected': {
                        backgroundColor: 'rgba(38, 137, 13, 0.08) !important',
                        '&:hover': {
                            backgroundColor: 'rgba(38, 137, 13, 0.12) !important'
                        }
                    }
                }
            }
        },
        MuiSelect: {
            styleOverrides: {
                root: {
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#26890D'
                    }
                }
            }
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    color: '#d9d9d9',
                    '&.Mui-checked': {
                        color: '#26890D'
                    },
                    '&:hover': {
                        backgroundColor: 'rgba(38, 137, 13, 0.04)'
                    }
                }
            }
        },
        MuiSwitch: {
            styleOverrides: {
                root: {
                    '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb': {
                        backgroundColor: '#26890D'
                    },
                    '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#26890D',
                        '&:hover': {
                            backgroundColor: '#26890D33'
                        }
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#26890D'
                    }
                },
                track: {
                    backgroundColor: '#00000099'
                },
                sizeSmall: {
                    width: 40, // Increased from 44 to make it wider like your image
                    height: 20,
                    padding: 0,
                    '& .MuiSwitch-switchBase': {
                        padding: 0,
                        margin: 2,
                        transitionDuration: '300ms',
                        '&.Mui-checked': {
                            transform: 'translateX(20px)', // Increased from 20px to reach the end properly
                            color: '#fff',
                            '& + .MuiSwitch-track': {
                                backgroundColor: 'var(--switch-bg-color)',
                                opacity: 1, // Remove opacity so border is clearly visible
                                border: '1px solid var(--switch-border-color)' // Increase border width
                            },
                            '& .MuiSwitch-thumb': {
                                backgroundColor: '#fff'
                            }
                        }
                    },
                    '& .MuiSwitch-thumb': {
                        backgroundColor: '#fff',
                        boxSizing: 'border-box',
                        width: 16,
                        height: 16,
                        boxShadow: 'none'
                    },
                    '& .MuiSwitch-track': {
                        borderRadius: 10,
                        height: 20,
                        backgroundColor: 'var(--switch-off-bg-color, #F8F9FA)', // Use dynamic off-state background
                        opacity: 1,
                        border: '1px solid var(--switch-off-border-color, #D6D9DC)', // Increase border width
                        transition: 'background-color 500ms, border-color 500ms',
                        boxSizing: 'border-box'
                    }
                }
            }
        }
    },
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 1280,
            lg: 1700,
            xl: 1920
        }
    },
});
